import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApp } from '@/hooks/use-app-context';
import { WriteModeSwitch } from '@/components/contribute/WriteModeSwitch';
import {
  TaskHeader,
  TaskNotice,
  TaskSessionComplete,
  TaskShell,
  fillCopy,
  kitLang,
  taskPosition,
} from '@/features/contribution-kit';
import {
  TranscriptionAudioActionBar,
  TranscriptionAudioBatchList,
  TranscriptionAudioSetup,
  TranscriptionAudioSourceCard,
  TranscriptionAudioWriteCard,
  TRANSCRIPTION_AUDIO_PAGE_SIZE,
  transcriptionAudioCopy,
  useTranscriptionAudioSession,
} from '@/features/transcription-audio';
import type { TranscriptionAudioSessionData } from '@/features/transcription-audio';
import { ValidationGateBanner, ValidationGateBlockedScreen, ValidationGateDialog } from '@/features/validation-gate';

/** "Transcrire": listen to recordings and write what is said. */
export default function TranscriptionAudio() {
  const { t, language } = useApp();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const currentLang = lang || language || 'fr';
  const copy = transcriptionAudioCopy[kitLang(currentLang)];
  const tr = t as unknown as Record<string, unknown>;
  const s = (key: string) => String(tr[key] ?? '');

  const fromSpeakContributeData = location.state as TranscriptionAudioSessionData;
  const session = useTranscriptionAudioSession(fromSpeakContributeData, location.state);

  const goHome = () => navigate(`/${currentLang}`);

  if (session.validationGate.isBlocked) {
    return <ValidationGateBlockedScreen />;
  }

  if (!session.sessionStarted) {
    return (
      <TranscriptionAudioSetup
        t={tr}
        copy={copy}
        languages={session.languages}
        selectedLanguage={session.selectedLanguage}
        onLanguageChange={session.setSelectedLanguage}
        source={session.source}
        onSourceChange={session.setSource}
        sessionResumeActive={session.sessionResumeActive}
        isLoading={session.isLoading}
        onStart={session.startLinkedSession}
        onBack={goHome}
      />
    );
  }

  if (session.sessionComplete) {
    return (
      <TaskSessionComplete
        title={s('sessionEnded')}
        description={copy.doneDescription}
        stats={[{ label: copy.doneSent, value: session.submittedCount, tone: session.submittedCount > 0 ? 'success' : 'default' }]}
        continueLabel={copy.doneBack}
        onContinue={session.dismissSessionComplete}
        onHome={goHome}
      />
    );
  }

  const { audios, currentIndex, page, total, current } = session;
  const position = taskPosition(currentIndex, audios.length);
  const absoluteIndex = (page - 1) * TRANSCRIPTION_AUDIO_PAGE_SIZE + position.current;
  const languageMeta = session.languages.find((item) => item.code === session.selectedLanguage);

  return (
    <TaskShell
      width="wide"
      header={
        <div className="space-y-4">
          <TaskHeader
            task="transcribe"
            index={currentIndex}
            total={audios.length}
            positionLabel={s('audioXofY').replace('{current}', String(position.current)).replace('{total}', String(position.total))}
            progress={session.progressPercent}
            itemId={current ? String(current.id) : null}
            onBack={goHome}
            meta={
              <>
                <span>{languageMeta?.displayName || languageMeta?.name || session.selectedLanguage}</span>
                <span>
                  {fillCopy(copy.pageMeta, { page })}
                  {typeof total === 'number' ? ` · ${absoluteIndex} / ${total}` : ''}
                </span>
              </>
            }
          />
          <WriteModeSwitch active="transcribe" />
        </div>
      }
      notices={<ValidationGateBanner />}
      actionBar={
        current ? (
        <TranscriptionAudioActionBar
          t={t}
          copy={copy}
          canGoPrevious={currentIndex > 0}
          hasAudio={current !== null}
          canSend={current !== null && session.text.trim().length > 0}
          isLoading={session.isLoading}
          isSubmitting={session.isSubmitting}
          onPrevious={session.previous}
          onSkip={session.skipAudio}
          onSend={session.submitTranscription}
          onTogglePlay={session.playAudio}
        />
        ) : null
      }
    >
      {session.blockedGate && (
        <ValidationGateDialog open onOpenChange={(open) => !open && session.clearBlockedGate()} gate={session.blockedGate} />
      )}

      {current ? (
        <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
          <div className="space-y-5">
            <TranscriptionAudioSourceCard
              t={tr}
              copy={copy}
              current={current}
              isPlaying={session.isPlaying}
              audioRef={session.audioRef}
              canvasRef={session.canvasRef}
              onPlayAudio={session.playAudio}
              onAudioEnded={session.onAudioEnded}
            />
            <TranscriptionAudioBatchList
              t={tr}
              copy={copy}
              audios={audios}
              currentIndex={currentIndex}
              page={page}
              limit={TRANSCRIPTION_AUDIO_PAGE_SIZE}
              hasMore={session.hasMore}
              isLoading={session.isLoading}
              onSelectAudio={session.selectAudio}
              onNextPage={session.nextPage}
            />
          </div>
          <TranscriptionAudioWriteCard
            t={t}
            copy={copy}
            text={session.text}
            onTextChange={session.setText}
            textFrancais={session.textFrancais}
            onTextFrancaisChange={session.setTextFrancais}
            showKeyboard={session.showKeyboard}
            onToggleKeyboard={() => session.setShowKeyboard(!session.showKeyboard)}
            isLoading={session.isLoading}
            hasRejectedAttempt={Boolean(current.hasRejectedAttempt)}
            rejectionReasons={current.rejectionReasons ?? []}
          />
        </div>
      ) : (
        <TaskNotice tone="info" icon="inbox" title={copy.emptyTitle}>
          {copy.emptyBody}
        </TaskNotice>
      )}
    </TaskShell>
  );
}
